import { arrayMax, Datalayer, defineDirective, groupBy, isNumber, LocaleString, sum } from "../../omnia"
import { IDatalayersService } from "../../types"

import m = omnia.models;
import { Chart } from "chart.js";

interface IYieldBand {
    value: number
    area: number
    cssColor: string
}

export class YieldDistributionCtrl {

    static create(): ng.IDirective {

        const def = defineDirective(YieldDistributionCtrl, "app/datalayers/distribution/yield/yield.html", {
            layer: "="
        })

        def.link = (scope, element: ng.IAugmentedJQuery, attrs, ctrl: YieldDistributionCtrl) => ctrl.link(element);

        return def

    }


    private _rootElt: ng.IAugmentedJQuery

    private layer: Datalayer;

    private chart: Chart

    private yields: IYieldBand[];

    private total: number;
    private effectiveYield: number;
    private maxArea: number;

    public columnStyle: { width: string }


    static $inject = ["$scope", "OmniaDatalayersService"];

    constructor(private $scope: ng.IScope, private svc: IDatalayersService) {
    }

    $onDestroy() {
        this.chart?.destroy()
        this.chart = undefined
    }

    private link(element: ng.IAugmentedJQuery) {
        this._rootElt = element
        this.setup()
    }

    private setup() {

        if (this._rootElt) {

            this.svc.ensureInit().then(r => {

                this.$scope.$watch("vm.layer", () => this.setupLayer())
                this.$scope.$on("distribution.refresh", () => this.setupLayer())

            });

        }

    }

    private setupLayer() {

        if (!this.layer) {
            return
        }

        const scale = this.getCustomScale()

        const yields = scale ? this.setupCustomScale(scale) : this.setupAutoScale()

        this.yields = yields.sort((a, b) => a.value > b.value ? 1 : a.value < b.value ? -1 : 0);

        this.effectiveYield = this.layer.datalayer.Value ? parseFloat(<string>this.layer.datalayer.Value) : undefined;

        this.maxArea = arrayMax(this.yields, d => d.area);
        this.total = sum(this.yields, d => Math.round(d.value * d.area));

        this.setupChart()


    }

    private setupCustomScale(scale: m.ICropYieldScale): IYieldBand[] {

        // TODO
        // - Utiliser directement les intervalles définis dans le scale
        // - Tant que plus de 8, fusionner l'intervalle le moins représentatif (plus petite surface) avec un intervalle adjacent?

        return this.setupAutoScale()

    }

    private setupAutoScale(): IYieldBand[] {

        let rounding = 0.5

        const numzones = this.layer.zones.filter(z => isNumber(z.value))

        const zones = numzones.map(z => ({
            area: z.area / 10000,
            value: this.roundX(z.value, rounding),
            originalValue: z.value
        }));

        rounding = .5;
        while (groupBy(zones, z => z.value).length > 8) {
            rounding += .5;
            zones.forEach(z => z.value = this.roundX(z.originalValue, rounding));
        }

        const yields = groupBy(zones, d => d.value).map(g => {
            return {
                area: Math.round(sum(g.values, v => v.area) * 10) / 10,
                value: g.key,
                cssColor: omnia.color.colorToCss(this.layer.viewer.getValueFillColor(g.key), false)
            }
        });

        return yields

    }

    private setupChart() {

        var labels = [];
        var data = [];
        var colors = [];

        var totalArea = sum(this.yields, d => d.area);

        this.yields.forEach(d => {

            data.push(Math.round((d.area / totalArea) * 100))
            labels.push(d.value)
            colors.push(d.cssColor)

        });

        var ctx = (this._rootElt[0].querySelector("canvas") as HTMLCanvasElement).getContext("2d");

        Chart.defaults.global.defaultFontFamily = '"Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif'
        Chart.defaults.global.defaultFontSize = 11

        this.updateColumnWidth(250, labels.length, false)

        this.chart?.destroy()

        this.chart = new Chart(ctx, {
            type: 'bar',
            data: {
                labels: labels,
                datasets: [{
                    data: data,
                    backgroundColor: colors,
                    label: 'Yield' as LocaleString
                }]
            },
            options: {
                title: {
                    display: true,
                    text: 'Yield distribution by area (t/ha)' as LocaleString
                },
                tooltips: {
                    enabled: false
                },
                legend: {
                    display: false
                },
                responsive: false,
                scales: {
                    xAxes: [{
                        // Note: it does not seem we can rely on afterFit, because its with property
                        // seems to change after it is called
                        // afterFit: (scaleInstance) => {
                        //     console.log("afterFit", scaleInstance)
                        //     this.updateColumnWidth(scaleInstance.width, scaleInstance.ticks.length, true)
                        // }
                    }],
                    yAxes: [{
                        ticks: {
                            min: 0,
                            callback: (value, index, values) => {
                                return value + '%';
                            }
                        },
                        afterFit: scaleInstance => {
                            scaleInstance.width = 40
                        }
                    }],
                }
            }
        });

    }

    private getCustomScale() {

        var scales = this.svc.getCropYieldScales(this.svc.yearId);
        var cropId = this.layer.cropId;
        if (!cropId) {
            var cropping = this.svc.getCropping(this.layer.croppingId);
            if (cropping) {
                cropId = cropping.cropId;
            }
        }

        if (cropId) {
            return scales.find(s => s.CropId === cropId);
        }

    }

    private getYield(value: number, area: number): number {
        let adjustment = area === this.maxArea && this.effectiveYield ? this.effectiveYield - this.total : 0
        adjustment = Math.round(adjustment * 100) / 100
        return Math.round(value * area) + adjustment
    }

    private roundX(value: number, x: number): number {
        const v = (value % x) >= x / 2 ? parseInt(<any>(value / x)) * x + x : parseInt(<any>(value / x)) * x
        return Math.round(v * 10) / 10
    }

    private updateColumnWidth(fullWidth: number, columns: number, apply: boolean) {

        this.columnStyle = null

        if (columns > 0) {
            const columnWidth = Math.floor(fullWidth / columns)
            this.columnStyle = { width: String(columnWidth) + "px" }
        }

        if (apply) {
            this.$scope.$applyAsync()
        }

    }

}
